import { designVariables } from "@studio3t/design-variables";
import { ButtonSmallSecondary } from "@studio3t/generic-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import styled from "styled-components";
import { reservationsApi } from "@/api/client";
import { JobStatusBadge } from "@/components/JobStatusBadge";
import {
  CellText,
  EmptyRowMessage,
  TableRow as ListTableRow,
  Table,
  TableCell,
  TableHead,
  TableHeader,
} from "@/components/ListTable";
import { SectionCard } from "@/components/SectionCard";
import { normalizeState } from "@/pages/pipelineHelpers";
import type { JobMetadata, ReservationStatus, Service } from "@/types/api";
import { ReleaseSlotModal } from "./ReleaseSlotModal";
import { type ReservationRow, reservationRow } from "./reservationRows";

const STATE_LABELS: Record<ReservationRow["state"], string> = {
  active: "Active",
  retained: "Retained",
  transferring: "Being taken over",
  adopting: "Being adopted",
  dropping: "Dropping",
  needs_reconciliation: "Needs reconciling",
  dropped: "Dropped",
};

/**
 * The slots shown on a service's page. A slot lives on the PostgreSQL server,
 * so that service lists every slot reserved through it; a change capture
 * service reads exactly one, so its page shows that one.
 */
export function slotsOf(
  service: Pick<Service, "name" | "provider">,
  reservations: ReservationStatus[],
): ReservationStatus[] {
  const key =
    service.provider === "postgres" ? "target_service" : "cdc_service";
  return reservations.filter(
    (status) => status.reservation[key] === service.name,
  );
}

/** Whether a provider's page carries the section at all. */
export function hasReplicationSlots(provider: Service["provider"]): boolean {
  return provider === "postgres" || provider === "postgres_cdc";
}

/**
 * The replication slots reserved on a PostgreSQL service, or the one a change
 * capture service reads. A retained slot outlives its deleted pipeline and
 * keeps its WAL on the server, so what each one costs is shown here, and
 * releasing one is an explicit decision.
 */
export function ReplicationSlotsSection({
  service,
  jobs,
  onJobClick,
}: {
  service: Pick<Service, "name" | "provider">;
  /** The pipelines that exist, for each owner's status; a retained slot's
   *  owner is among them no more. */
  jobs: JobMetadata[] | undefined;
  onJobClick: (jobName: string) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ["reservations"],
    queryFn: reservationsApi.list,
    refetchInterval: 15_000,
  });
  const [selected, setSelected] = useState<ReservationRow | null>(null);
  const rows = slotsOf(service, data ?? []).map(reservationRow);
  const onServer = service.provider === "postgres";
  const jobByName = new Map((jobs ?? []).map((job) => [job.name, job]));

  return (
    <SectionCard title={onServer ? "Replication slots" : "Replication slot"}>
      <TableWrap>
        <Table>
          <SlotColumns onServer={onServer} />
          <tbody>
            {rows.map((row) => (
              <SlotRow
                key={row.id}
                row={row}
                onServer={onServer}
                job={jobByName.get(row.expectedOwner.job)}
                onJobClick={onJobClick}
                onDecide={setSelected}
              />
            ))}
          </tbody>
        </Table>
        {!isLoading && rows.length === 0 && (
          <EmptyRowMessage>
            {error instanceof Error ? error.message : emptyMessage(onServer)}
          </EmptyRowMessage>
        )}
      </TableWrap>
      <ReleaseSlotModal
        row={selected}
        onClose={() => {
          setSelected(null);
          queryClient.invalidateQueries({ queryKey: ["reservations"] });
        }}
      />
    </SectionCard>
  );
}

// Column widths and headings. The slot and its owner carry the names; the
// figures need little.
function SlotColumns({ onServer }: { onServer: boolean }) {
  return (
    <>
      <colgroup>
        <col style={{ width: "26%" }} />
        {onServer && <col style={{ width: "16%" }} />}
        <col style={{ width: "18%" }} />
        <col />
        <col />
        <col style={{ width: "9%" }} />
        <col style={{ width: "11%" }} />
        {/* 120 mirrors the used-by table's Status column. */}
        <col style={{ width: 120 }} />
      </colgroup>
      <TableHead>
        <TableRow>
          <TableHeader>Slot</TableHeader>
          {onServer && <TableHeader>Change capture</TableHeader>}
          <TableHeader>Owner</TableHeader>
          <TableHeader>State</TableHeader>
          <TableHeader>On the server</TableHeader>
          <TableHeader>Lag</TableHeader>
          <TableHeader>Retained WAL</TableHeader>
          <TableHeader>Status</TableHeader>
        </TableRow>
      </TableHead>
    </>
  );
}

// One slot. The row opens its owner's pipeline while that exists.
function SlotRow({
  row,
  onServer,
  job,
  onJobClick,
  onDecide,
}: {
  row: ReservationRow;
  onServer: boolean;
  job: JobMetadata | undefined;
  onJobClick: (jobName: string) => void;
  onDecide: (row: ReservationRow) => void;
}) {
  const open = job ? () => onJobClick(job.name) : undefined;
  return (
    <TableRow
      $static={!job}
      tabIndex={job ? 0 : undefined}
      onClick={open}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && opensRow(e.key)) {
          e.preventDefault();
          open?.();
        }
      }}
    >
      <TableCell>
        <CellText title={row.id}>{row.slot}</CellText>
      </TableCell>
      {onServer && (
        <TableCell>
          <CellText>{row.cdcService}</CellText>
        </TableCell>
      )}
      <TableCell>
        <CellText title={row.owner}>{row.expectedOwner.job}</CellText>
      </TableCell>
      <TableCell>
        <CellText title={row.reason}>{STATE_LABELS[row.state]}</CellText>
      </TableCell>
      <TableCell>
        <CellText title={row.reason}>{row.slotLabel}</CellText>
      </TableCell>
      <TableCell>
        <CellText>{row.lag ?? "—"}</CellText>
      </TableCell>
      <TableCell>
        <CellText>{row.retained ?? "—"}</CellText>
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <StatusSlot>
          <SlotStatus row={row} job={job} onDecide={onDecide} />
        </StatusSlot>
      </TableCell>
    </TableRow>
  );
}

function opensRow(key: string): boolean {
  return key === "Enter" || key === " ";
}

// The owner's status while it exists; once it is gone, the decision its
// slot is waiting for.
function SlotStatus({
  row,
  job,
  onDecide,
}: {
  row: ReservationRow;
  job: JobMetadata | undefined;
  onDecide: (row: ReservationRow) => void;
}) {
  if (job) return <JobStatusBadge state={normalizeState(job.state)} />;
  const decision = slotDecision(row);
  if (!decision) return null;
  return <ButtonSmallSecondary text={decision} onClick={() => onDecide(row)} />;
}

function slotDecision(row: ReservationRow): string | undefined {
  if (row.canDrop) return "Release";
  return row.needsReconciliation ? "Reconcile" : undefined;
}

function emptyMessage(onServer: boolean): string {
  return onServer
    ? "No change capture pipeline has reserved a slot on this server yet."
    : "This service's pipeline has not reserved its slot yet: the slot is created on the first start.";
}

// The card owns the border; the top line is the header/table divider.
const TableWrap = styled.div`
  border-top: 1px solid ${(props) => props.theme.cards1Border};
`;

// A row opens its owner's pipeline page, as the used-by rows do. A retained
// slot's owner is gone, so its row is not a link.
const TableRow = styled(ListTableRow)<{ $static?: boolean }>`
  ${(props) => props.$static && "cursor: default;"}
`;

// The badge sits as it does in the used-by table's Status column.
const StatusSlot = styled.div`
  padding-left: ${designVariables.ui.table.tbody.cell.gap}px;

  & > * {
    display: inline-flex;
  }
`;
